/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, TextField, Stack, Typography } from '@mui/material';
import moment from 'moment';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import { DatePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import ApprovedMedRepList from './components/ApprovedMedRepList';
import { getAppointments, getMedRepsApproved } from '../../api/approvalsApi';
import AppointmentCalendar from '../../components/AppontmentCalendar';
import AppointmentStatTable from './components/AppointmentStatTable';
import AppointmentListModal from './components/AppointmentListModal';

export default function Approved() {
  const [tableParams, setTableParams] = useState({
    rep_id: '',
    current_date: moment().format('YYYY-MM-DD'),
  });

  const [calendarParams, setCalendarParams] = useState({
    currentDate: moment().format('YYYY-MM-DD'),
    date: '',
    rep_id: '',
  });

  const [appointmentModal, setAppointmentModal] = useState({
    toggle: false,
    data: [],
  });

  const [medRepDropdown, setMedRepDropdown] = useState([]);

  const [appointments, setAppointments] = useState({
    data: [],
    totalRows: 0,
    loading: false,
  });

  const [scheduledAppointments, setScheduledAppointments] = useState({
    data: [],
    date: '',
    totalRows: 0,
    toggle: false,
  });

  const loadSpecialist = async () => {
    try {
      const res = await getMedRepsApproved({
        rep_id: '',
        date: moment().format('YYYY-MM'),
      });

      const { success, data, message } = res.data;

      if (!success) {
        toast.error(message);
        return;
      }

      setMedRepDropdown(data.map((row) => ({ label: row.label, value: row.unique_id })));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadAppointments = async (params) => {
    try {
      toast.loading('Loading calendar...');
      setAppointments({ ...appointments, data: [], totalRows: 0, loading: true });

      const res = await getAppointments({
        ...params,
        approval_id: 1,
        date_submitted: '',
      });
      toast.dismiss();
      setAppointments({ ...appointments, data: [], totalRows: 0, loading: false });

      const { success, data, totalRows, message } = res.data;

      if (!success) {
        toast.error(message);
        return;
      }

      const newData = data.map((row) => ({
        ...row,
        date: row.date_scheduled,
        date_time: `${row.date_scheduled} ${row.time_scheduled}`,
      }));

      setAppointments({
        data: newData,
        totalRows,
        loading: false,
      });
    } catch (error) {
      setAppointments({ ...appointments, data: [], totalRows: 0, loading: false });

      toast.error(error.message);
    }
  };

  useEffect(() => {
    loadSpecialist();
  }, []);

  useEffect(() => {
    setScheduledAppointments({
      ...scheduledAppointments,
      date: '',
      data: appointments.data,
      totalRows: appointments.totalRows,
    });
  }, [appointments]);

  const handleChangeMonth = (value) =>
    setTableParams({ ...tableParams, current_date: moment(value).format('YYYY-MM-DD') });

  const handleChangeSpecialist = (newValue) => {
    setTableParams({ ...tableParams, rep_id: newValue?.value });
  };

  const handleClickSpecialist = (data) => {
    const newCalendarParams = {
      ...calendarParams,
      date: moment(data.date_scheduled).format('YYYY-MM'),
      currentDate: data.date_scheduled,
      rep_id: data.unique_id,
    };

    setCalendarParams(newCalendarParams);
    loadAppointments(newCalendarParams);
  };

  const handleChangeDate = (date) => {
    const newCalendarParams = {
      ...calendarParams,
      date: moment(date).format('YYYY-MM'),
      currentDate: date,
    };
    setCalendarParams(newCalendarParams);
    loadAppointments(newCalendarParams);
  };

  const handleShowMore = (data) => {
    setAppointmentModal({
      ...appointmentModal,
      toggle: true,
      data: data.appointments,
    });
  };

  const handleAppointmentModalClose = () => {
    setAppointmentModal({
      ...appointmentModal,
      toggle: false,
      data: [],
    });
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Stack direction='row' spacing={2} sx={{ mb: 3 }}>
          <div style={{ width: '300px' }}>
            <Autocomplete
              disablePortal
              id='med-rep-filter'
              fullWidth
              options={medRepDropdown}
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(params) => <TextField {...params} label='Specialist' />}
              size='small'
              onChange={(e, newValue) => handleChangeSpecialist(newValue)}
            />
          </div>
          <div>
            <DatePicker
              label='"month" and "year"'
              views={['month', 'year']}
              value={tableParams.current_date}
              renderInput={(params) => <TextField {...params} label='Month' size='small' />}
              onChange={handleChangeMonth}
            />
          </div>
        </Stack>

        <Grid container spacing={2}>
          <Grid xs={6}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Approved CWP
            </Typography>

            <div style={{ marginBottom: 3, position: 'relative' }}>
              <ApprovedMedRepList
                selectedDate={tableParams.current_date}
                selectedMedRepId={tableParams.rep_id}
                onClickSpecialist={handleClickSpecialist}
              />
            </div>
          </Grid>
          <Grid xs={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Calendar with MD Name
              </Typography>
              <AppointmentCalendar
                initDate={calendarParams.currentDate}
                appointments={appointments.data}
                onChangeDate={handleChangeDate}
                onShowMore={handleShowMore}
              />
            </Box>
          </Grid>
          <Grid xs={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Calendar with Scheduled Appointments
              </Typography>
              <AppointmentCalendar
                initDate={calendarParams.currentDate}
                appointments={appointments.data}
                onChangeDate={handleChangeDate}
                isCount
                onShowMore={handleShowMore}
                selectedDate={scheduledAppointments.date}
              />
            </Box>
          </Grid>
          <Grid xs={6}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Scheduled Appointment per MD
            </Typography>
            <div style={{ marginBottom: 3, height: '550px', position: 'relative' }}>
              <AppointmentStatTable
                approvalId='1'
                date={calendarParams.date}
                dateSubmitted={calendarParams.currentDate}
                repId={calendarParams.rep_id}
              />
            </div>
          </Grid>
        </Grid>
      </Box>
      <AppointmentListModal
        toggle={appointmentModal.toggle}
        data={appointmentModal.data}
        onClose={handleAppointmentModalClose}
      />
    </>
  );
}
